import { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/Card";
import { Switch } from "@/components/ui/Switch";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import useCurrentAccount from "@/components/context/useCurrentAccount";

interface ReportSubscription {
  subscribed: boolean;
  account_enabled: boolean;
}

export default function WeeklyReportsSection() {
  const [subscription, setSubscription] = useState<ReportSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);

  const { team } = useCurrentAccount({ requireLoggedIn: true });
  const isAdmin = team?.role === "admin";

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/web_api/reports/subscription", {
        credentials: "include",
      });

      if (!response.ok) {
        let errorMessage = `Failed to load report settings (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setSubscription(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load report settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccountToggle = useCallback(async (enabled: boolean) => {
    try {
      setSaving(true);
      setError(null);
      setSavedField(null);

      const response = await fetch("/web_api/reports/account_setting", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ enabled }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update setting");
      }

      setSubscription((prev) => (prev ? { ...prev, account_enabled: enabled } : prev));
      setSavedField("account");
      setTimeout(() => setSavedField(null), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update setting.");
    } finally {
      setSaving(false);
    }
  }, []);

  const handleUserToggle = useCallback(async (subscribed: boolean) => {
    try {
      setSaving(true);
      setError(null);
      setSavedField(null);

      const response = await fetch("/web_api/reports/subscription", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subscribed }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update subscription");
      }

      setSubscription((prev) => (prev ? { ...prev, subscribed } : prev));
      setSavedField("user");
      setTimeout(() => setSavedField(null), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update subscription.");
    } finally {
      setSaving(false);
    }
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Weekly Uptime Reports</CardTitle>
          <CardDescription>Receive weekly email summaries of your monitors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Weekly Uptime Reports</CardTitle>
          <CardDescription>Receive weekly email summaries of your monitors</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error || "Failed to load report settings"}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Weekly Uptime Reports</CardTitle>
        <CardDescription>
          Get a weekly summary of uptime, downtime, and incidents for your monitors every Monday
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isAdmin && (
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="report-account" className="flex flex-col flex-1 cursor-pointer">
              <span className="text-sm font-medium leading-none">
                Enable weekly reports for this workspace
              </span>
              <span className="text-sm text-muted-foreground mt-1">
                When enabled, all workspace members will receive weekly uptime reports by email.
              </span>
            </label>
            <div className="flex items-center gap-3">
              <SavedCheckmark visible={savedField === "account"} />
              <Switch
                id="report-account"
                checked={subscription.account_enabled}
                onCheckedChange={handleAccountToggle}
                disabled={saving}
                className="mt-0.5"
              />
            </div>
          </div>
        )}

        {subscription.account_enabled && (
          <div
            className={`flex items-center justify-between gap-4 ${isAdmin ? "pt-4 border-t" : ""}`}
          >
            <label htmlFor="report-user" className="flex flex-col flex-1 cursor-pointer">
              <span className="text-sm font-medium leading-none">Receive weekly reports</span>
              <span className="text-sm text-muted-foreground mt-1">
                Unsubscribe from weekly reports for your email address only.
              </span>
            </label>
            <div className="flex items-center gap-3">
              <SavedCheckmark visible={savedField === "user"} />
              <Switch
                id="report-user"
                checked={subscription.subscribed}
                onCheckedChange={handleUserToggle}
                disabled={saving}
                className="mt-0.5"
              />
            </div>
          </div>
        )}

        {!subscription.account_enabled && !isAdmin && (
          <p className="text-sm text-muted-foreground">
            Weekly reports are currently disabled for this workspace. Ask a workspace admin to
            enable them.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function SavedCheckmark({ visible }: { visible: boolean }) {
  return (
    <svg
      className={`w-5 h-5 text-success transition-opacity duration-200 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path d="M5 13l4 4L19 7"></path>
    </svg>
  );
}
